import { faChevronLeft, faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchBoardingCountersThunk, NewBookingsStatus } from '@spike/new-bookings-action';
import { BoardingCounters } from '@spike/new-booking-model';
import { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Moment } from 'moment-timezone';
import { useApiClientWrapper } from 'hooks';
import { RootState } from 'store';
import Spinner from 'components/UI/V3/Spinner';
import { DailyBoarding } from './DailyBoarding';
import styles from './Availability.module.scss';

const SCROLL_STEP_PX = 200;

export interface AvailabilityProps {
    from: Moment;
    to: Moment;
}

export const Availability: FunctionComponent<AvailabilityProps> = ({ from, to }) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const scrollRef = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const fromStart = from.clone().startOf('day');
    const selectedToStart = to.clone().startOf('day');
    const toStartForService = selectedToStart.clone().subtract(1, 'day');

    const startDateKey = fromStart.format('YYYY-MM-DD');
    const endDateKey = toStartForService.format('YYYY-MM-DD');

    const boardingCounters = useSelector<RootState, Array<BoardingCounters>>(
        state => state.newBookings.boardingCounters
    );
    const status = useSelector((state: RootState) => state.newBookings.status);

    const isLoading = status === NewBookingsStatus.FetchingBoardingCounters;

    const updateScrollState = useCallback(() => {
        const el = scrollRef.current;
        if (!el) {
            return;
        }
        const { scrollLeft, scrollWidth, clientWidth } = el;
        setCanScrollLeft(scrollLeft > 2);
        setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 2);
    }, []);

    useEffect(() => {
        dispatch(fetchBoardingCountersThunk(apiClientWrapper, fromStart, toStartForService));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, startDateKey, endDateKey]);

    useEffect(() => {
        updateScrollState();
        const el = scrollRef.current;
        if (!el) {
            return undefined;
        }
        el.addEventListener('scroll', updateScrollState, { passive: true });
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateScrollState) : null;
        ro?.observe(el);
        return () => {
            el.removeEventListener('scroll', updateScrollState);
            ro?.disconnect();
        };
    }, [boardingCounters, updateScrollState]);

    const scrollBy = (delta: number) => {
        scrollRef.current?.scrollBy({ left: delta, behavior: 'smooth' });
    };

    const items = boardingCounters.filter((c): c is BoardingCounters & { date: Moment } => {
        if (!c.date) {
            return false;
        }
        const day = c.date.clone().startOf('day');
        return !day.isBefore(fromStart, 'day') && !day.isAfter(toStartForService, 'day');
    });

    return (
        <section
            className={styles.root}
            aria-label="Availability"
        >
            <div className={styles.header}>
                <div className={styles.title}>Availability</div>
                <div className={styles.nav}>
                    <button
                        type="button"
                        className={styles.navButton}
                        aria-label="Scroll availability left"
                        disabled={!canScrollLeft}
                        onClick={() => scrollBy(-SCROLL_STEP_PX)}
                    >
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                    <button
                        type="button"
                        className={styles.navButton}
                        aria-label="Scroll availability right"
                        disabled={!canScrollRight}
                        onClick={() => scrollBy(SCROLL_STEP_PX)}
                    >
                        <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                </div>
            </div>
            {isLoading && items.length === 0 ? (
                <div className={styles.loading}>
                    <Spinner />
                </div>
            ) : (
                <div className={styles.trackWrap}>
                    <div
                        ref={scrollRef}
                        className={styles.track}
                    >
                        {items.map(c => (
                            <DailyBoarding
                                key={c.date.format('YYYY-MM-DD')}
                                date={c.date}
                                bookingsCount={c.bookingsCount ?? 0}
                                occupancyPercentage={c.occupancyPercentage ?? 0}
                                assignedUnitsTotal={c.assignedUnitsTotal ?? 0}
                                unassignedUnitsTotal={c.unassignedUnitsTotal ?? 0}
                            />
                        ))}
                    </div>
                </div>
            )}
        </section>
    );
};
